import React, { useRef, useEffect } from 'react';
import { Building2 } from 'lucide-react';

const companiesData = [
  { name: 'TCS', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/95/TCS_Logo.svg' },
  { name: 'Infosys', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/95/Infosys_logo.svg' },
  { name: 'Wipro', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a0/Wipro_Primary_Logo_Color_RGB.svg' },
  { name: 'Cognizant', logo: 'https://upload.wikimedia.org/wikipedia/commons/6/6a/Cognizant_logo_2022.svg' },
  { name: 'HCL', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/f7/HCLTech_logo.svg' },
  { name: 'Accenture', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Accenture.svg' },
  { name: 'IBM', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg' },
  { name: 'Amazon', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg' },
  { name: 'Microsoft', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg' },
  { name: 'Google', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg' }
];

const CompaniesPage = () => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const scroll = scrollRef.current;
    let scrollPos = 0;

    const animate = () => {
      if (scroll) {
        scrollPos += 0.5;
        if (scrollPos >= scroll.scrollWidth / 2) {
          scrollPos = 0;
        }
        scroll.scrollLeft = scrollPos;
      }
    };

    const interval = setInterval(animate, 20);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-16 sm:py-24 bg-gray-950 relative overflow-hidden min-h-screen pt-20 sm:pt-32 border-y border-red-600/20">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-orange-600 rounded-full filter blur-3xl animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">
              Placement Partners
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
            Our Students Work At{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
              Top Companies
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            <span className="text-red-500 font-bold">30,000+</span> elite hackers trained and working in leading organizations worldwide
          </p>
        </div>

        <div className="relative">
          <div 
            ref={scrollRef}
            className="flex overflow-x-hidden scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex">
              {[...companiesData, ...companiesData, ...companiesData].map((company, index) => (
                <div 
                  key={`company-${index}`}
                  className="flex-shrink-0 mx-6 group"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 to-orange-600/20 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-all"></div>
                    <div className="relative bg-white rounded-xl p-8 w-48 h-32 flex items-center justify-center hover:scale-105 transition-transform duration-300 border-2 border-transparent group-hover:border-red-500/50">
                      <img
                        src={company.logo}
                        alt={company.name}
                        className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden items-center justify-center absolute inset-0">
                        <span className="text-gray-800 font-bold text-xl">{company.name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-16 max-w-4xl mx-auto">
            <div className="bg-gray-900/50 border border-red-600/30 rounded-xl p-6 text-center hover:border-red-500 transition-all">
              <div className="text-4xl font-black text-red-500 mb-2">30,000+</div>
              <div className="text-gray-400">Students Trained</div>
            </div>
            <div className="bg-gray-900/50 border border-red-600/30 rounded-xl p-6 text-center hover:border-red-500 transition-all">
              <div className="text-4xl font-black text-red-500 mb-2">500+</div>
              <div className="text-gray-400">Hiring Partners</div>
            </div>
            <div className="bg-gray-900/50 border border-red-600/30 rounded-xl p-6 text-center hover:border-red-500 transition-all">
              <div className="text-4xl font-black text-red-500 mb-2">12 LPA</div>
              <div className="text-gray-400">Avg. Package</div>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-br from-gray-900 to-gray-950 border border-red-500/30 rounded-3xl p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Join These{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
              Top Companies?
            </span>
          </h3>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Get expert training, build real-world skills, and land your dream cybersecurity job with our placement assistance program
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-xl shadow-red-500/25"
          >
            Explore Our Courses
          </button>
        </div>
      </div>

      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-950 to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-950 to-transparent z-10 pointer-events-none"></div>
    </section>
  );
};

export default CompaniesPage;
